import { html, type Html, on, Page, rune, View } from 'rune-ts';
import klass from './PopupStoreList.module.scss';
import { LayoutView } from '../../components/cells/Layout/LayoutView';
import { footer_links } from '../../components/cells/Footer/constant';
import { typo, typoPcMo } from '../../../shared/typography/typo';
import { classes, htmlIf } from '../../../shared/util';
import {
  PopupStoreItemCardType,
  PopupStoreListType,
  PopupStorePagination,
} from '../../../features/PopupStores/type';
import { EmptyList } from './views/EmptyList';
import { ArchivedList } from './views/ArchivedList';
import { NowList } from './views/NowList';
import { BaseLayoutOptions } from '../../../features/Core/service';
import { Modal } from '../../components/atoms/Modal/Modal';
import { OpenSubscribeModalEvent } from '../../../features/PopupStores/service/events';
import { ButtonAction } from '../../../shared/components/atoms/ButtonAction/ButtonAction';
import { Alert } from '../../components/cells/Alert/Alert';
import { addOpenRunList } from '../../../features/PopupStores/api';
import { ArrowRightIcon } from '../../components/atoms/Icon';
import { CoreUiS } from '../../../features/Core/outbound/share';
import { SectionTabs } from '../../../shared/components/atoms/SectionTabs/SectionTabs';
import { applySubHeaderHeadroom } from '@services/MarppleShop/renderApp/components/cells/Header/libs/helpers/headroom.ts';
import { Header } from '@services/MarppleShop/renderApp/components/cells/Header/Global/Header.ts';
import { TabListView } from '@services/MarppleShop/shared/components/cells/TabList/TabListView.ts';

export interface PopupStoreCount {
  total: number;
  archived: number;
  now: number;
  upcoming: number;
}

export type PopupStoreListPageData = {
  type: PopupStoreListType;
  popup_stores: PopupStoreItemCardType[];
  last_popups: PopupStoreItemCardType[];
  pagination: PopupStorePagination;
  counts: PopupStoreCount;
  layout_options: BaseLayoutOptions;
};

interface OpenRunFormProps {
  item: PopupStoreItemCardType | null;
  modal: Modal;
}

class OpenRunForm extends View<OpenRunFormProps> {
  override template() {
    const is_mobile = !!rune.getSharedData(this)?.is_mobile;
    return html`
      <div class="${klass.subscribe_form}" style="height: 30000px;">
        <div class="${klass.form_body}">
          <div class="${klass.form_section}">
            <h4 class="${typoPcMo({ is_mobile, pc: '16_bold', mo: '14_bold' })}">
              ${ET('mps2::popup_store::openrun_modal::name_label')}
            </h4>
            <p class="${typoPcMo({ is_mobile, pc: '16_medium', mo: '14_medium' })}">
              ${this.data.item?.title}
            </p>
          </div>

          <div class="${klass.form_section}">
            <h4 class="${typoPcMo({ is_mobile, pc: '16_bold', mo: '14_bold' })}">
              ${ET('mps2::popup_store::openrun_modal::alarm_label')}
            </h4>
            <p class="${typoPcMo({ is_mobile, pc: '16_medium', mo: '14_medium' })}">
              ${ET('mps2::popup_store::openrun_modal::email_label')}
            </p>
            <input
              type="email"
              name="email"
              placeholder="${ET('mps2::popup_store::openrun_modal::email_label')}"
              class="${klass.form_input} ${typoPcMo({ is_mobile, pc: '16_medium', mo: '14_medium' })}"
            />
          </div>
          <ul class="${klass.form_guide} ${typoPcMo({ is_mobile, pc: '14_medium', mo: '14_medium' })}">
            <li>${ET('mps2::popup_store::openrun_modal::notice_li_1')}</li>
            <li>${ET('mps2::popup_store::openrun_modal::notice_li_2')}</li>
            <li>${ET('mps2::popup_store::openrun_modal::notice_li_3')}</li>
          </ul>
        </div>

        <div class="${klass.form_action}">
          ${new ButtonAction({
            label: ET('mps2::popup_store::openrun_modal::cancel_btn'),
            type: 'line',
            size: 'large',
            submit: false,
            klass: 'open_run_modal_close_btn',
          })}
          ${new ButtonAction({
            label: ET('mps2::popup_store::openrun_modal::confirm_btn'),
            type: 'blue',
            size: 'large',
            submit: false,
            klass: 'open_run_modal_submit_btn',
          })}
        </div>
      </div>
    `;
  }

  @on('click', `.open_run_modal_close_btn`)
  onClickCancel() {
    this.data.modal.close();
  }

  @on('click', `.open_run_modal_submit_btn`)
  async onClickSubmit() {
    const email = this.element().querySelector('input')?.value;
    const regex = /^([0-9a-zA-Z_.-]+)@([0-9a-zA-Z_-]+)(\.[0-9a-zA-Z_-]+){1,2}$/;

    if (!email) {
      await Alert.open({ message: ET('mps2::popup_store::openrun_modal::valid_email') });
      return;
    }

    if (!regex.test(email)) {
      await Alert.open({ message: ET('mps2::popup_store::openrun_modal::valid_email2') });
      return;
    }

    try {
      await addOpenRunList({ popup_id: this.data.item!.id, email });
      await Alert.open({
        title: ET('mps2::popup_store::openrun_modal::alert_title'),
        message: ET('mps2::popup_store::openrun_modal::alert_msg'),
      });
      this.data.modal.close();
    } catch (err: any) {
      Alert.open({ message: err.message });
    }
  }
}

class PopupStoreListBody extends View<PopupStoreListPageData> {
  private readonly sectionTabs: SectionTabs;

  tooltip = html`
    <div class="${klass.tooltip}">
      <p class="${typo('14_medium')}">${html.preventEscape(ET('mps2::popup_store::tooltip'))}</p>
      <a
        class="${klass.tooltip_link} ${typo('16_bold')}"
        href="/${ET.lang}/@/popupstore/online"
        target="_blank"
      >
        ${html.preventEscape(ET('mps2::popup_store::tooltip_link'))} ${ArrowRightIcon}
      </a>
    </div>
  `;

  constructor(props: PopupStoreListPageData) {
    super(props);
    const { type } = props;
    const { archived, now } = props.counts;

    this.sectionTabs = new SectionTabs({
      size: props.layout_options.is_mobile ? 'xSmall' : 'small',
      tabs: [
        {
          id: 'now',
          href: `/${ET.lang}/@/popup-store?tab=now`,
          label: 'Now',
          count: now,
        },
        {
          id: 'archived',
          href: `/${ET.lang}/@/popup-store?tab=archived`,
          label: 'Archived',
          count: archived,
        },
      ],
      current_tab_id: type,
      custom_klass: klass.section_tabs,
      variants: props.layout_options.is_mobile ? 'bubble' : undefined,
    });
  }

  override template() {
    const {
      layout_options: { is_mobile },
      type,
      popup_stores,
      last_popups,
      pagination,
    } = this.data;

    return html`
      <div class="${klass.popupstore}">
        <!-- page title -->
        ${is_mobile
          ? new TabListView({
              tabs: [
                {
                  tab_title: ET('mps2::section_title::section_tabs::popup_store'),
                  href: `/${ET.lang}/@/popup-store`,
                  selected: true,
                },
                {
                  tab_title: ET('mps2::section_title::section_tabs::curation'),
                  href: `/${ET.lang}/@/curations`,
                  selected: false,
                },
                {
                  tab_title: ET('mps2::section_title::section_tabs::creator_view'),
                  href: `/${ET.lang}/@/creator_views`,
                  selected: false,
                },
              ],
            })
          : html`<div class="${klass.title}">${CoreUiS.makePopupStoreTitle(is_mobile, 'popup-store')}</div>`}

        <div class="${klass.tabs}">${this.sectionTabs}</div>

        <div class="${klass.list}">
          <!--  나우 리스트  -->
          ${htmlIf(
            new NowList({ items: popup_stores, is_mobile, type }),
            !!popup_stores.length && type === 'now',
          )}

          <!--  아카이브 리스트  -->
          ${htmlIf(
            new ArchivedList({ items: popup_stores, pagination, is_mobile }),
            !!popup_stores.length && type === 'archived',
          )}

          <!--  업커밍 리스트 ( 추후 스펙 확정되면 다시 추가될 예정, 현재는 삭제 (views/UpcomingList) )  -->

          <!--  Empty 리스트  -->
          ${htmlIf(new EmptyList({ last_popups, is_mobile, type }), !popup_stores.length)}
        </div>
      </div>
    `;
  }

  override onMount() {
    if (this.data.layout_options.is_mobile) {
      applySubHeaderHeadroom(Header, this.sectionTabs);
    }
  }
}

export class PopupStoreListPage extends Page<PopupStoreListPageData> {
  subscribeModal: Modal = new Modal({
    title: ET('mps2::popup_store::openrun_modal::title'),
    body: html``,
    isOpen: false,
    size: this.data.layout_options.is_mobile ? 'medium' : 'large',
    is_bottom_sheet: this.data.layout_options.is_mobile,
  });

  protected override template(data: PopupStoreListPageData): Html {
    const { layout_options } = data;

    return html`
      <div class="${classes(klass.page)}">
        ${new LayoutView(
          {
            body: new PopupStoreListBody(this.data),
            footer_data: { ...footer_links, lang: ET.lang, is_mobile: layout_options.is_mobile },
          },
          {
            ...layout_options,
            full_width: layout_options.is_mobile,
            has_bottom_tab_bar: true,
            selected_bottom_tab_idx: 2,
          },
        )}
        ${this.subscribeModal}
      </div>
    `;
  }

  protected override onRender() {
    this.addEventListener(OpenSubscribeModalEvent, (e) => {
      const { item } = e.detail;
      this.subscribeModal.data.body = new OpenRunForm({ item, modal: this.subscribeModal });
      this.subscribeModal.redraw();
      this.subscribeModal.open();
    });
  }
}
